/**
 * Thin `api()` helpers for the SMS consent endpoints — plain calls outside
 * Refine, same as CompanyInformationPage.tsx already does for its
 * settings-shaped data. Deliberately not a Refine resource: this isn't a
 * CRUD-list-shaped concept, it's a per-tenant status the messaging flow and
 * two staff views (TenantForm's panel, TenantList's pill) read directly.
 */

import type {
  SetOwnSmsConsentInput,
  SetSmsConsentInput,
  SmsConsentSource,
  SmsConsentStatus,
} from 'shared';
import { api } from './api.js';

export interface SmsConsentDto {
  tenantId: string;
  status: SmsConsentStatus;
  source: SmsConsentSource | null;
  reason: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  /** Tenant.phone now. */
  phone: string | null;
  /** The server-built disclosure to show — echoed back on save. Null with no phone on file. */
  disclosure: string | null;
  officePhone: string | null;
  /** The number and text the recorded decision covered. */
  consentedPhone: string | null;
  consentedDisclosure: string | null;
}

export function fetchSmsConsent(tenantId: string): Promise<SmsConsentDto> {
  return api<SmsConsentDto>(`/tenants/${tenantId}/sms-consent`);
}

export function saveSmsConsent(
  tenantId: string,
  input: SetSmsConsentInput,
): Promise<SmsConsentDto> {
  return api<SmsConsentDto>(`/tenants/${tenantId}/sms-consent`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/** The tenant's own consent status, resolved server-side from their
 *  session — no tenantId to pass, and no way to target anyone else's. */
export function fetchOwnSmsConsent(): Promise<SmsConsentDto> {
  return api<SmsConsentDto>('/tenants/me/sms-consent');
}

export function saveOwnSmsConsent(input: SetOwnSmsConsentInput): Promise<SmsConsentDto> {
  return api<SmsConsentDto>('/tenants/me/sms-consent', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

export function fetchSmsConsentList(params: {
  page: number;
  pageSize: number;
  search?: string | undefined;
  includeArchived?: boolean | undefined;
}): Promise<{ data: SmsConsentDto[]; total: number }> {
  const query = new URLSearchParams({
    page: String(params.page),
    pageSize: String(params.pageSize),
  });
  if (params.search) query.set('search', params.search);
  if (params.includeArchived) query.set('includeArchived', 'true');
  return api<{ data: SmsConsentDto[]; total: number }>(`/tenants/sms-consent?${query.toString()}`);
}
